import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const PANELS = [
  {
    eyebrow: "AI Engineering",
    title: "Intelligent systems, ready for production.",
    body: "We design retrieval pipelines, multi-agent orchestrations, and on-device inference. Built with GPT, Claude, and Gemini — wired into your real data.",
    image:
      "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=1920&q=80",
  },
  {
    eyebrow: "Web Platforms",
    title: "Performance you can feel.",
    body: "100-on-Lighthouse marketing sites. Hardened SaaS dashboards. Real-time platforms. We pair the best of Next.js, FastAPI, and edge compute.",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1920&q=80",
  },
  {
    eyebrow: "Mobile Apps",
    title: "Native craft. Cross-platform reach.",
    body: "Swift and Kotlin where it matters. React Native and Flutter where it scales. Built for the App Store charts and your enterprise IT.",
    image:
      "https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&w=1920&q=80",
  },
];

function Panel({ panel, index, progress }) {
  const total = 3;
  const slot = 1 / total;
  const start = index * slot;
  const end = (index + 1) * slot;
  const fade = slot * 0.18;
  const opacity = useTransform(
    progress,
    [start - fade, start + fade, end - fade, end + fade],
    [0, 1, 1, 0]
  );
  const scale = useTransform(progress, [start, end], [0.96, 1.04]);
  const y = useTransform(progress, [start, end], [40, -40]);

  return (
    <motion.div
      style={{ opacity }}
      className="absolute inset-0 flex items-center"
    >
      <div className="max-w-[1400px] w-full mx-auto px-6 md:px-10 grid grid-cols-12 gap-10 items-center">
        <motion.div style={{ y }} className="col-span-5 z-10">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#0A84FF] mb-4">
            {panel.eyebrow}
          </p>
          <h3 className="font-display text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.02] text-grad">
            {panel.title}
          </h3>
          <p className="mt-6 text-[#a1a1a6] text-base md:text-lg leading-relaxed max-w-md">
            {panel.body}
          </p>
        </motion.div>
        <motion.div style={{ scale }} className="col-span-7 relative">
          <div className="relative aspect-[16/10] rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(10,132,255,0.35)]">
            <img
              src={panel.image}
              alt={panel.title}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-black/30" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
              <span className="glass rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-white/80">
                {panel.eyebrow}
              </span>
              <span className="glass rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-white/80">
                Live
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

function StickyDesktop() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  return (
    <section ref={ref} className="relative h-[320vh] hidden lg:block">
      <div className="sticky top-0 h-screen overflow-hidden">
        <div className="aurora bg-[#0A84FF] h-[500px] w-[500px] -top-40 left-1/4 opacity-30" />
        <div className="aurora bg-[#F59E0B] h-[300px] w-[300px] bottom-10 right-1/4 opacity-15" />
        {PANELS.map((p, i) => (
          <Panel key={p.title} panel={p} index={i} progress={scrollYProgress} />
        ))}
      </div>
    </section>
  );
}

function StackedMobile() {
  return (
    <section className="relative py-16 md:py-24 lg:hidden">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 space-y-10 md:space-y-16">
        {PANELS.map((p, i) => (
          <motion.article
            key={p.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-center"
          >
            <div className="relative aspect-[16/10] rounded-2xl md:rounded-3xl overflow-hidden border border-white/10 shadow-[0_20px_60px_-15px_rgba(10,132,255,0.4)] order-1">
              <img
                src={p.image}
                alt={p.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-black/30" />
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                <span className="glass rounded-full px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] text-white/80">
                  {p.eyebrow}
                </span>
                <span className="glass rounded-full px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] text-white/80">
                  Live
                </span>
              </div>
            </div>
            <div className="order-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#0A84FF] mb-3">
                {p.eyebrow}
              </p>
              <h3 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-[-0.03em] leading-[1.05] text-grad">
                {p.title}
              </h3>
              <p className="mt-4 text-[#a1a1a6] text-sm sm:text-base leading-relaxed">
                {p.body}
              </p>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

export default function StickyShowcase() {
  return (
    <>
      <StackedMobile />
      <StickyDesktop />
    </>
  );
}
